import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Search, X } from 'lucide-react';
import api, { absoluteAsset } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import ImageUpload from '../../components/ImageUpload';
import ExcelImport from '../../components/ExcelImport';
import { useToast } from '../../hooks/use-toast';

const emptyForm = { id: '', name: '', category: '', logo: '' };

const BrandsAdmin = () => {
  const { toast } = useToast();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([api.get('/brands'), api.get('/categories')]);
      setBrands(b.data);
      setCategories(c.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ ...emptyForm, category: categories[0]?.name || '' });
    setOpen(true);
  };
  const openEdit = (b) => { setForm({ ...b }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category) {
      toast({ title: 'Completa nombre y categoría' });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), category: form.category, logo: form.logo || '' };
      if (form.id) await api.put(`/brands/${form.id}`, payload);
      else await api.post('/brands', payload);
      toast({ title: form.id ? 'Marca actualizada' : 'Marca creada' });
      setOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/brands/${deleting.id}`);
      toast({ title: 'Marca eliminada' });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo eliminar' });
    }
  };

  const filtered = brands.filter(b =>
    !query.trim() || b.name.toLowerCase().includes(query.trim().toLowerCase()) || b.category.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Marcas</h2>
          <p className="text-sm text-slate-500">{brands.length} en total</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..." className="h-10 w-full border-slate-200 bg-white pl-9 sm:w-64" />
          </div>
          <Button onClick={openNew} className="h-10 gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
            <Plus className="h-4 w-4" /> Nueva marca
          </Button>
        </div>
      </div>

      <ExcelImport entity="brands" onImported={load} />

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 py-16 text-center text-slate-500">No hay marcas.</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Logo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Categoría</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(b => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex h-12 w-16 items-center justify-center overflow-hidden rounded border border-slate-100 bg-slate-50 p-1">
                      {b.logo ? <img src={absoluteAsset(b.logo)} alt={b.name} className="h-full w-full object-contain" /> : <span className="text-xs text-slate-400">s/logo</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="bg-emerald-50 text-emerald-800 hover:bg-emerald-50">{b.category}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(b)} className="h-8 gap-1 border-slate-300">
                        <Pencil className="h-3.5 w-3.5" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDeleting(b)} className="h-8 gap-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej. Tabasco" className="h-11" required />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Selecciona una categoría" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ImageUpload label="Logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="gap-1"><X className="h-4 w-4" /> Cancelar</Button>
              <Button type="submit" disabled={saving} className="gap-2 bg-emerald-700 text-white hover:bg-emerald-800">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar marca?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleting?.name}</strong> y todos sus productos. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BrandsAdmin;
